/**
 * Store details rendered from API sections (compound structure).
 * Renders sections in API order; each section type has a dedicated sub-component.
 */
import React, {useMemo} from 'react';
import {Store, StoreSection} from '../../models/store.model';
import {CreditInfo} from '../../models/creditInfo.model';
import {AppCardTitle} from '../../../../constants';
import {CreditInfoCard} from '../cards/CreditInfoCard';
import {formatCurrency} from '../../../../Presentation/utils/numberUtils';
import {
  STORE_SECTION_FIELD_LABELS,
  formatSectionValue,
  getSectionDataEntries
} from '../../utils/storeSectionLabels';

// -----------------------------------------------------------------------------
// Compound: Section card wrapper (title + content)
// -----------------------------------------------------------------------------
interface SectionCardProps {
  title: string;
  children: React.ReactNode;
}

function SectionCard({title, children}: SectionCardProps) {
  return (
    <div>
      <AppCardTitle className="text-gray-900 border-b pb-2 mb-4">{title}</AppCardTitle>
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Section block: Credit info (uses existing CreditInfoCard)
// -----------------------------------------------------------------------------
function CreditInfoSectionBlock({section, storeId}: {section: StoreSection; storeId: number}) {
  const data = section.data as unknown as CreditInfo;
  if (!data || typeof data.totalDue !== 'number') return null;
  return (
    <SectionCard title={section.title}>
      <CreditInfoCard creditInfo={data} storeId={storeId} />
    </SectionCard>
  );
}

// -----------------------------------------------------------------------------
// Section block: Images (profile + gallery)
// -----------------------------------------------------------------------------
function ImagesSectionBlock({section}: {section: StoreSection}) {
  const profileUrl = section.data?.profileImageUrl as string | undefined;
  const images = (section.data?.images as string[] | undefined) ?? [];
  return (
    <SectionCard title={section.title}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {profileUrl && (
          <div>
            <p className="text-xs text-gray-500 mb-1">الصورة الشخصية</p>
            <img src={profileUrl} alt="الملف الشخصي" className="rounded-lg object-cover w-full h-32" />
          </div>
        )}
        {images.map((url, i) => (
          <div key={i}>
            <p className="text-xs text-gray-500 mb-1">صورة {i + 1}</p>
            <img src={url} alt={`صورة ${i + 1}`} className="rounded-lg object-cover w-full h-32" />
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// -----------------------------------------------------------------------------
// Section block: Documents (commercial register + tax card)
// -----------------------------------------------------------------------------
function DocumentsSectionBlock({section}: {section: StoreSection}) {
  const crNumber = section.data?.commercialRegisterNumber as string | undefined;
  const crImage = section.data?.commercialRegisterImage as string | undefined;
  const taxNumber = section.data?.taxCardNumber as string | undefined;
  const taxImage = section.data?.taxCardImage as string | undefined;
  return (
    <SectionCard title={section.title}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {crNumber != null && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">رقم السجل التجاري</p>
            <p className="font-medium" dir="ltr">{crNumber}</p>
            {crImage && (
              <img src={crImage} alt="السجل التجاري" className="mt-2 rounded max-h-40 object-contain" />
            )}
          </div>
        )}
        {taxNumber != null && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">رقم البطاقة الضريبية</p>
            <p className="font-medium" dir="ltr">{taxNumber}</p>
            {taxImage && (
              <img src={taxImage} alt="البطاقة الضريبية" className="mt-2 rounded max-h-40 object-contain" />
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

// -----------------------------------------------------------------------------
// Section block: Generic key-value grid (basicInfo, location, contact, etc.)
// -----------------------------------------------------------------------------
function GenericDataSectionBlock({section}: {section: StoreSection}) {
  const entries = getSectionDataEntries(section.data as Record<string, unknown>);
  return (
    <SectionCard title={section.title}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {entries.map(([key, value]) => (
          <div key={key} className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">{STORE_SECTION_FIELD_LABELS[key] ?? key}</p>
            <p className="font-medium text-gray-900 break-words">
              {key === 'totalDue' || key === 'totalPaid'
                ? formatCurrency(Number(value))
                : formatSectionValue(value)}
            </p>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// -----------------------------------------------------------------------------
// Section renderer: picks the right block by section.id
// -----------------------------------------------------------------------------
function renderSectionBlock(section: StoreSection, storeId: number): React.ReactNode {
  switch (section.id) {
    case 'creditInfo':
      return <CreditInfoSectionBlock key={section.id} section={section} storeId={storeId} />;
    case 'images':
      return <ImagesSectionBlock key={section.id} section={section} />;
    case 'documents':
      return <DocumentsSectionBlock key={section.id} section={section} />;
    default:
      return <GenericDataSectionBlock key={section.id} section={section} />;
  }
}

// -----------------------------------------------------------------------------
// Public compound component
// -----------------------------------------------------------------------------
export interface StoreDetailsBySectionsProps {
  store: Store;
  childrenAfterSections?: React.ReactNode;
}

export const StoreDetailsBySections: React.FC<StoreDetailsBySectionsProps> = ({
  store,
  childrenAfterSections
}) => {
  const sortedSections = useMemo(() => {
    const sections = store.sections ?? [];
    return [...sections].sort((a, b) => a.order - b.order);
  }, [store.sections]);

  if (sortedSections.length === 0) return null;

  return (
    <div className="space-y-6">
      {sortedSections.map((section) => renderSectionBlock(section, store.id))}
      {childrenAfterSections}
    </div>
  );
};
